import {
  Component,
  Input,
  SimpleChanges,
  OnChanges,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Swiper from 'swiper';
import { Navigation, Pagination, Controller } from 'swiper/modules';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Subscription } from 'rxjs/internal/Subscription';
import { ENGLISH_LOCALE } from 'src/app/common/_services/charts.service';
import moment from 'moment';

Swiper.use([Navigation, Pagination, Controller]);

export interface SowingChartData {
  time: string[];
  barley: number[];
  cotton: number[];
  maize: number[];
  potato: number[];
  rapeseed: number[];
  riceindica: number[];
  ricejaponica: number[];
  sorghum: number[];
  soybean: number[];
  sugarbeets: number[];
  wheat: number[];
}

export const SowingCrops: string[] = [
  'barley',
  'cotton',
  'maize',
  'potato',
  'rapeseed',
  'riceindica',
  'ricejaponica',
  'sorghum',
  'soybean',
  'wheat',
  'sugarbeets',
];

@Component({
  selector: 'app-chart-seminabilita',
  templateUrl: './chart-seminabilita.component.html',
  styleUrls: [
    '../../../assets/css/charts_css/reset.css',
    '../../../assets/css/charts_css/style.css',
    '../../../assets/css/charts_css/tab-seminabilita.css',
    '../../../assets/css/charts_css/swiper.css',
  ],
  standalone: false,
})
export class ChartSeminabilitaComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input()
  chartData: SowingChartData; // Input data for chart rendering

  dailyDataSet$: BehaviorSubject<SowingChartData> = new BehaviorSubject(null);
  dayTitle$: BehaviorSubject<string> = new BehaviorSubject('');
  selectedIndex: number = 0;
  maxDayIndex: number = 0;
  crops: string[] = SowingCrops;

  selectedDay$: BehaviorSubject<number> = new BehaviorSubject(0);

  private dataByDay: Map<string, SowingChartData> = new Map();
  private _swiperCrops: Swiper[];
  private _swiperTime: Swiper;
  private readonly subscriptions: Subscription = new Subscription();

  constructor(private readonly translate: TranslateService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.chartData && changes.chartData.currentValue !== null) {
      this.dataByDay = this.mapDataByDay(); // Group chart data by date
      this.selectedDay$.next(0); // Reset selected day to first
    }
  }

  ngAfterViewInit(): void {
    const daySubscription = this.selectedDay$.subscribe((index: number) => {
      if (this.chartData && this.dataByDay) {
        // Save the max index to prevent out-of-bound access
        const keys = Array.from(this.dataByDay.keys());
        this.maxDayIndex = keys.length - 1;
        const key = keys[index];
        const values = Array.from(this.dataByDay.values());
        const subset = values[index];
        // Emit the data of the selected day
        this.dailyDataSet$.next(subset);
        // Update the displayed title
        this.setTitle(key);
      }
      // Show current hour only on current day
      this.selectedIndex = index === 0 ? moment().hour() : -1;
    });
    this.subscriptions.add(daySubscription);

    const dailyDatasetSubscription = this.dailyDataSet$.subscribe((dataset: SowingChartData) => {
      if (dataset) {
        this.createSliders();
      }
    });
    this.subscriptions.add(dailyDatasetSubscription);
  }

  /**
   * Formats and sets the title of the currently selected day.
   *
   * @param timestamp - Date string to be formatted and displayed.
   */
  private setTitle(timestamp: string) {
    const date = moment(timestamp, 'DD/MM/YYYY').locale(ENGLISH_LOCALE);
    const day = date.format('DD');
    const month = this.translate.instant('months.' + date.format('MMMM').toLowerCase());
    const year = date.format('YYYY');
    // Publish the formatted date
    this.dayTitle$.next(`${day} ${month} ${year}`);
  }

  /**
   * Initializes and configures Swiper sliders for crops and time.
   */
  createSliders() {
    // Destroy existing sliders to avoid duplicates
    if (this._swiperCrops) {
      this._swiperCrops.forEach((swiper) => swiper.destroy());
    }

    if (this._swiperTime) {
      this._swiperTime.destroy();
    }

    // Create a swiper for each crop
    this._swiperCrops = SowingCrops.map((crop) => document.getElementById('swiper_' + crop)).map(
      (element) =>
        new Swiper(element, {
          slidesPerView: 24,
          spaceBetween: 2,
          simulateTouch: true,
          freeMode: true,
          breakpoints: {
            320: { slidesPerView: 6 },
            480: { slidesPerView: 6 },
            640: { slidesPerView: 6 },
            768: { slidesPerView: 8 },
            960: { slidesPerView: 11 },
            1000: { slidesPerView: 18 },
            1200: { slidesPerView: 24 },
          },
          controller: {
            control: this._swiperTime,
          },
          observer: true,
          observeParents: true,
        })
    );

    // Create the time swiper which controls crop swipers
    this._swiperTime = new Swiper('.swiper-container.slider-h-seminabilita', {
      slidesPerView: 24,
      spaceBetween: 2,
      simulateTouch: true,
      freeMode: true,
      breakpoints: {
        320: { slidesPerView: 6 },
        480: { slidesPerView: 6 },
        640: { slidesPerView: 6 },
        768: { slidesPerView: 8 },
        960: { slidesPerView: 11 },
        1000: { slidesPerView: 18 },
        1200: { slidesPerView: 24 },
      },
      controller: {
        control: this._swiperCrops,
      },
      observer: true,
      observeParents: true,
    });
  }

  /**
   * Maps the incoming chart data into a structure grouped by day.
   *
   * @returns A map of date strings to corresponding sowing chart data.
   */
  private mapDataByDay(): Map<string, SowingChartData> {
    const map = new Map<string, SowingChartData>();

    // Iterate over each time entry and group related crop values by date
    this.chartData.time.forEach((item, index) => {
      const day = moment(item).format('DD/MM/YYYY');

      // If the date is not in the map yet, initialize the structure
      if (!map.has(day)) {
        const sowingData = {} as SowingChartData;
        for (const attribute of ['time', 'key', ...SowingCrops]) {
          sowingData[attribute] = [];
        }
        map.set(day, sowingData);
      }

      // Append the time and crop data to the appropriate day
      map.get(day).time.push(item);
      for (const crop of SowingCrops) {
        map.get(day)[crop].push(this.chartData[crop][index]);
      }
    });
    return map;
  }

  /**
   * Decrements the selected day index.
   */
  previousDay() {
    let index = this.selectedDay$.getValue();
    index -= 1;
    // Go to the previous day
    this.selectedDay$.next(index);
  }

  /**
   * Increments the selected day index.
   */
  nextDay() {
    let index = this.selectedDay$.getValue();
    index += 1;
    // Go to the next day
    this.selectedDay$.next(index);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
